import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_HOVER_DELAY_MS = 250

/**
 * Custom hook for managing hover card visibility with debounced show/hide behavior.
 *
 * @returns {Object} Hook return value
 * @returns {boolean} return.isVisible - Whether the hover card is currently visible
 * @returns {Object} return.triggerProps - Props to spread on the trigger element
 * @returns {Object} return.cardProps - Props to spread on the hover card element
 *
 * @example
 * ```jsx
 * const { isVisible, triggerProps, cardProps } = useHoverCard()
 *
 * return (
 *   <div {...triggerProps}>
 *     <Button>Hover me</Button>
 *     {isVisible && (
 *       <div {...cardProps}>
 *         <CardContent />
 *       </div>
 *     )}
 *   </div>
 * )
 * ```
 */
export const useHoverCard = () => {
  const [isVisible, setIsVisible] = useState(false)
  const showTimeoutRef = useRef(null)
  const hideTimeoutRef = useRef(null)

  // Clear all timeouts on unmount
  useEffect(() => () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    // Start show timeout
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, DEFAULT_HOVER_DELAY_MS)
  }, [])

  const handleMouseLeave = useCallback(() => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }

    // Start hide timeout
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, DEFAULT_HOVER_DELAY_MS)
  }, [])

  const triggerProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  }

  return {
    isVisible,
    triggerProps,
  }
}
